import type { CSSProperties } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { encodePeek, recordPath, resolveOpenMode, type Peekable } from '@monrdv/shared';
import { usePreferences } from '../lib/preferences.js';

/**
 * A link to a record.
 *
 * **Every** reference to a customer, job, quote, invoice or payment goes through this. That
 * is the point: it is the single place that knows how this user wants records opened, so no
 * screen has to decide, and the behaviour is consistent everywhere.
 *
 * Three behaviours, chosen by preference and overridable per click:
 *
 *   page — navigate to the record
 *   peek — open it in a side panel, leaving the current screen exactly where it is
 *   tab  — open a new browser tab
 *
 * It renders a real `<a href>` in every mode, which matters more than it looks: the browser
 * gets a URL it can copy, open in a new tab, or show in the status bar, and middle-click and
 * ⌘-click work without us implementing them. Peek intercepts the plain click only.
 */

export type RecordLinkProps = {
  kind: Peekable;
  id: string;
  children: ReactNode;
  className?: string;
  /** Inline styles, for the screens that style that way rather than with classes. */
  style?: CSSProperties;
  /** Force a mode regardless of preference — for an explicit "open full page" control. */
  mode?: 'page' | 'peek' | 'tab';
  title?: string;
  onOpened?: () => void;
};

export function RecordLink({ kind, id, children, className, style, mode, title, onOpened }: RecordLinkProps) {
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const href = recordPath(kind, id);

  const openPeek = () => {
    // The panel target lives in the URL, so a reload or a shared link restores it. Replace
    // rather than push: peeking three customers in a row should not put three entries in
    // history that the back button has to walk through.
    void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, peek: encodePeek(kind, id) }), replace: true });
    onOpened?.();
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const chosen = mode ?? resolveOpenMode(preferences.recordOpenMode, event);

    // Let the browser handle a new tab natively: it is better at it than we are, and the
    // href is already correct.
    if (chosen === 'tab') return;

    event.preventDefault();
    if (chosen === 'peek') {
      openPeek();
    } else {
      void navigate({ to: href });
      onOpened?.();
    }
  };

  // Middle-click does not fire onClick, and the browser's own handling of it is right.
  return (
    <Link
      to={href}
      className={className}
      style={style}
      title={title}
      onClick={handleClick}
      target={mode === 'tab' ? '_blank' : undefined}
      rel={mode === 'tab' ? 'noreferrer' : undefined}
    >
      {children}
    </Link>
  );
}

/**
 * An explicit "open in the side panel" affordance, for rows whose main click does something
 * else — a table row that navigates, with a peek button on the end.
 */
export function PeekButton({ kind, id, label }: { kind: Peekable; id: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="peek-btn"
      aria-label={label ?? 'Aperçu'}
      title={label ?? 'Aperçu'}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, peek: encodePeek(kind, id) }), replace: true });
      }}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="2" y="3" width="12" height="10" rx="1.5" />
        <path d="M10 3v10" />
      </svg>
    </button>
  );
}
